import {
  PopoverButton as HeadlessPopoverButton,
  PopoverButtonProps as HeadlessPopoverButtonProps,
} from "@headlessui/react"
import { ChevronDown } from "lucide-react"
import { ReactNode, useId, RefObject } from "react"

import { classNames } from "../../../../lib/classNames"
import { Label } from "../../../base/informational/Label"
import { Icon, IconSizes } from "../../../Icon"

interface PopoverSelectButtonProps
  extends Omit<HeadlessPopoverButtonProps, "className" | "content"> {
  placeholder: string
  label?: string
  title?: string
  accessory?: ReactNode
  // If defined, content is rendered instead of accessory + title.
  content?: ReactNode
  invalid?: boolean
  errorMessage?: string
  className?: string
  containerClassName?: string
  size?: "small" | "large"
  hideChevron?: boolean
  ref?: RefObject<HTMLButtonElement | null>
}

const PopoverSelectButton = (props: PopoverSelectButtonProps) => {
  const {
    ref,
    placeholder,
    label,
    title,
    accessory,
    content,
    invalid,
    errorMessage,
    className,
    containerClassName,
    disabled,
    size = "large",
    hideChevron,
    ...otherProps
  } = props

  const id = useId()

  return (
    <Label
      label={label}
      inputId={id}
      errorMessage={errorMessage}
      containerClassName={classNames(
        "flex w-full flex-col",
        containerClassName
      )}
    >
      <HeadlessPopoverButton
        id={id}
        className={classNames(
          "bg-v2-background-elevation-raised border-v2-border-default color-default text-body shadow-control rounded-control group flex h-10 w-full items-center gap-x-2 border px-3",
          "hover:not-disabled:border-v2-border-strong",
          "disabled:border-v2-border-default-disabled disabled:bg-v2-background-surface-tonal disabled:text-v2-foreground-default-disabled",
          size === "small" && "h-7.5",
          (invalid || !!errorMessage) &&
            "border-v2-border-danger hover:not-disabled:border-v2-border-danger",
          className
        )}
        disabled={disabled}
        {...otherProps}
        ref={ref}
      >
        {content ? (
          <div className="flex min-w-0 flex-1 items-center">{content}</div>
        ) : (
          <>
            {accessory}
            <span
              className={classNames(
                "flex-1 truncate whitespace-nowrap text-left",
                title ? "color-default" : "text-v2-foreground-default-weaker",
                "group-disabled:text-v2-foreground-default-disabled"
              )}
            >
              {title ?? placeholder}
            </span>
          </>
        )}
        {!hideChevron && (
          <Icon
            icon={ChevronDown}
            size={IconSizes.ExtraSmall}
            className="text-v2-foreground-default-weak group-disabled:text-v2-foreground-default-disabled"
          />
        )}
      </HeadlessPopoverButton>
    </Label>
  )
}

PopoverSelectButton.displayName = "PopoverSelectButton"

export default PopoverSelectButton
